// src/pages/offers/components/OfferHistoryModal.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  listOfferHistory,
  getOfferItemsHistory,
  getOfferServicesAtRevision,
  buildOfferSnapshotFromAuditRow,
} from "../../../utils/api-offers";
import OfferViewModal from "./OfferViewModal";

/** Fecha DD/MM/AAAA HH:mm (preferencia del usuario) */
const fmtDateTime = (iso) =>
  new Date(iso).toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

function DiffRow({ label, oldVal, newVal, money = false }) {
  const same = (oldVal ?? null) === (newVal ?? null);
  if (same) return null;
  const fmt = (v) => {
    if (v === null || v === undefined) return "—";
    if (money) {
      const n = Number(v);
      return Number.isFinite(n)
        ? n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : String(v);
    }
    return String(v);
  };
  return (
    <div className="text-sm flex items-center gap-2">
      <span className="font-medium">{label}:</span>
      <span className="line-through opacity-70">{fmt(oldVal)}</span>
      <span>→</span>
      <span className="font-semibold">{fmt(newVal)}</span>
    </div>
  );
}

/** Calcular totales si faltan en el snapshot */
function calcTotals(services = [], vatRate = 0.21) {
  const subtotal = services.reduce(
    (s, it) => s + Number(it.qty || 0) * Number(it.price || 0),
    0
  );
  const tax = +(subtotal * vatRate).toFixed(2);
  const total = +(subtotal + tax).toFixed(2);
  return { subtotal, tax, total };
}

/** Derivar número de revisión con múltiples fuentes */
function getRevisionNumber(row, offer) {
  // 1) Campo explícito
  const direct = row?.revision ?? row?.rev;
  if (direct != null && direct !== "") return String(direct);

  // 2) Cambios en el código: diff.code.new / diff.code.old
  const fromDiffNew = row?.diff?.code?.new;
  const fromDiffOld = row?.diff?.code?.old;
  const parsedFromDiff =
    parseRevFromCode(fromDiffNew) ?? parseRevFromCode(fromDiffOld);
  if (parsedFromDiff != null) return parsedFromDiff;

  // 3) Código actual de la oferta como último recurso
  const parsedFromOffer = parseRevFromCode(offer?.code);
  if (parsedFromOffer != null) return parsedFromOffer;

  return null;
}

/** Extrae 'NN' de patrones tipo CT-5605-00, CT-1234-02, etc. */
function parseRevFromCode(code) {
  if (!code) return null;
  const m = String(code).match(/-[0-9]{2}$/); // termina en -NN
  if (!m) return null;
  return m[0].replace("-", ""); // "00" | "01" ...
}

export default function OfferHistoryModal({ offer, onClose }) {
  const [tab, setTab] = useState("general"); // 'general' | 'items'
  const [loadingGeneral, setLoadingGeneral] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [general, setGeneral] = useState([]); // offer_audit
  const [items, setItems] = useState([]); // offer_services_audit

  const PAGE = 15;
  const [shownGeneral, setShownGeneral] = useState(PAGE);
  const [shownItems, setShownItems] = useState(PAGE);

  // Modal snapshot
  const [snapOpen, setSnapOpen] = useState(false);
  const [snapOffer, setSnapOffer] = useState(null);
  const [snapLoading, setSnapLoading] = useState(false);

  // Cerrar con ESC
  const handleKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose?.();
    },
    [onClose]
  );
  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  useEffect(() => {
    if (!offer?.id) return;

    setLoadingGeneral(true);
    listOfferHistory(offer.id)
      .then((data) => setGeneral(Array.isArray(data) ? data : []))
      .finally(() => setLoadingGeneral(false));

    setLoadingItems(true);
    getOfferItemsHistory(offer.id, { limit: 300 })
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .finally(() => setLoadingItems(false));

    setShownGeneral(PAGE);
    setShownItems(PAGE);
  }, [offer?.id]);

  const ITEM_KEYS = ["description", "qty", "price"];
  const itemsFiltered = useMemo(() => {
    return (items ?? []).map((row) => {
      const diffs = [];
      ITEM_KEYS.forEach((k) => {
        const node = row?.diff?.[k];
        if (!node) return;
        diffs.push({ key: k, old: node.old ?? null, new: node.new ?? null });
      });
      return { ...row, _diffs: diffs };
    });
  }, [items]);

  /** Abre el snapshot de la revisión con reconstrucción "as of" si hace falta */
  async function openRevisionSnapshot(row) {
    setSnapLoading(true);
    try {
      // 1) Base desde audit + oferta actual como fallback
      let base = buildOfferSnapshotFromAuditRow(row, offer);

      // 2) Reconstruir services si faltan o vienen vacíos
      let services =
        Array.isArray(base.services) && base.services.length > 0
          ? base.services
          : await getOfferServicesAtRevision(offer.id, row.changed_at);

      // 3) Normalizar/ordenar services
      services = (services || [])
        .map((s, i) => ({
          id: s.id ?? s.service_id ?? `tmp_${i}`,
          description: (s.description ?? s.desc ?? "").trim(),
          qty: Number(s.qty ?? s.quantity ?? 1),
          price: Number(s.price ?? s.unit_price ?? 0),
          position: Number(s.position ?? s.order ?? i),
          instrument_id: s.instrument_id ?? null,
        }))
        .sort((a, b) => (a.position ?? 9999) - (b.position ?? 9999));

      // 4) Totales si faltan
      let { subtotal, tax, total } = {
        subtotal: base.subtotal,
        tax: base.tax,
        total: base.total,
      };
      const vat = (() => {
        const raw = base.iva_rate;
        const n = Number(raw);
        return (n > 1 ? n / 100 : n) || 0.21;
      })();
      if (subtotal == null || tax == null || total == null) {
        const t = calcTotals(services, vat);
        subtotal = t.subtotal;
        tax = t.tax;
        total = t.total;
      }

      // 5) Ensamblar snapshot final
      const snap = {
        ...base,
        services,
        subtotal,
        tax,
        total,
      };

      setSnapOffer(snap);
      setSnapOpen(true);
    } finally {
      setSnapLoading(false);
    }
  }

  // Cerrar al clickear el fondo (pero no el contenido)
  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        onMouseDown={onBackdropClick}
      >
        <div
          className="w-full max-w-4xl bg-[#0F1115] text-white rounded-2xl shadow-2xl overflow-hidden"
          style={{ maxHeight: "85vh" }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-[#0F1115]/95 backdrop-blur px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <h2 className="text-lg font-semibold">Historial — {offer?.code ?? "Oferta"}</h2>
            <button
              className="px-3 py-1 rounded-lg bg-white text-black hover:opacity-90 transition"
              onClick={onClose}
            >
              Cerrar
            </button>
          </div>

          {/* Tabs */}
          <div className="sticky top-[56px] z-10 bg-[#0F1115]/95 backdrop-blur px-6 py-3 border-b border-white/10 flex gap-2">
            <button
              className={`px-3 py-1 rounded-lg ${
                tab === "general" ? "bg-white text-black" : "bg-white/10 hover:bg-white/15"
              }`}
              onClick={() => setTab("general")}
            >
              Revisiones
            </button>
            <button
              className={`px-3 py-1 rounded-lg ${
                tab === "items" ? "bg-white text-black" : "bg-white/10 hover:bg-white/15"
              }`}
              onClick={() => setTab("items")}
            >
              Ítems (cambios)
            </button>
          </div>

          {/* Contenido */}
          <div
            className="px-6 py-4 overflow-y-auto"
            style={{ maxHeight: "calc(85vh - 104px)" }}
          >
            {tab === "general" ? (
              <>
                {loadingGeneral ? (
                  <div className="opacity-70">Cargando…</div>
                ) : (general?.length ?? 0) === 0 ? (
                  <div className="opacity-70">Sin registros.</div>
                ) : (
                  <>
                    <ul className="space-y-3">
                      {general.slice(0, shownGeneral).map((row, idx) => {
                        const totalOld = row?.diff?.total?.old ?? null;
                        const totalNew = row?.diff?.total?.new ?? null;

                        // Fallback robusto para la revisión
                        const revRaw = getRevisionNumber(row, offer);
                        const revision = revRaw != null ? String(revRaw).padStart(2, "0") : "—";

                        return (
                          <li
                            key={`${row.id ?? row.changed_at ?? idx}`}
                            className="rounded-xl bg-white/5 p-4"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="font-semibold">
                                {row.action} • Rev {revision}
                              </div>
                              <div className="text-sm opacity-70">
                                {fmtDateTime(row.changed_at)}
                              </div>
                            </div>

                            <div className="flex flex-col gap-2">
                              <DiffRow label="Total" oldVal={totalOld} newVal={totalNew} money />

                              <div className="flex flex-wrap gap-2 pt-1">
                                <button
                                  className="px-3 py-1.5 rounded-md bg-white text-black text-sm hover:opacity-90 disabled:opacity-60"
                                  onClick={() => openRevisionSnapshot(row)}
                                  disabled={snapLoading}
                                >
                                  {snapLoading ? "Abriendo…" : "Ver revisión"}
                                </button>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>

                    {shownGeneral < general.length && (
                      <div className="flex justify-center mt-4">
                        <button
                          className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15"
                          onClick={() => setShownGeneral((n) => n + PAGE)}
                        >
                          Mostrar más
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                {loadingItems ? (
                  <div className="opacity-70">Cargando…</div>
                ) : (itemsFiltered?.length ?? 0) === 0 ? (
                  <div className="opacity-70">Sin cambios en ítems.</div>
                ) : (
                  <>
                    <ul className="space-y-3">
                      {itemsFiltered.slice(0, shownItems).map((row, idx) => {
                        const hasAny = (row._diffs ?? []).some(
                          (d) => (d.old ?? null) !== (d.new ?? null)
                        );
                        if (!hasAny) return null;
                        return (
                          <li
                            key={`${row.service_id ?? idx}-${row.changed_at}`}
                            className="rounded-xl bg-white/5 p-4"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="font-semibold">{row.action}</div>
                              <div className="text-sm opacity-70">{fmtDateTime(row.changed_at)}</div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {row.diff?.description &&
                                row.diff.description.old !== row.diff.description.new && (
                                  <DiffRow
                                    label="Descripción"
                                    oldVal={row.diff.description.old}
                                    newVal={row.diff.description.new}
                                  />
                                )}
                              {row.diff?.qty &&
                                row.diff.qty.old !== row.diff.qty.new && (
                                  <DiffRow
                                    label="Cantidad"
                                    oldVal={row.diff.qty.old}
                                    newVal={row.diff.qty.new}
                                  />
                                )}
                              {row.diff?.price &&
                                row.diff.price.old !== row.diff.price.new && (
                                  <DiffRow
                                    label="Precio unitario"
                                    oldVal={row.diff.price.old}
                                    newVal={row.diff.price.new}
                                    money
                                  />
                                )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>

                    {shownItems < itemsFiltered.length && (
                      <div className="flex justify-center mt-4">
                        <button
                          className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15"
                          onClick={() => setShownItems((n) => n + PAGE)}
                        >
                          Mostrar más
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 z-10 bg-[#0F1115] px-6 py-3 border-t border-white/10 flex justify-end">
            <button
              className="px-4 py-2 rounded-lg bg-white text-black hover:opacity-90"
              onClick={onClose}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>

      {/* Modal que muestra la oferta tal como estaba en esa revisión */}
      <OfferViewModal open={snapOpen} onClose={() => setSnapOpen(false)} offer={snapOffer} />
    </>
  );
}
